import { useState } from 'react';
import { bulkUpdateServiceRequestVisibility } from 'src/services/client';

interface UseServiceRequestVisibilityApiState {
  isSubmitting: boolean;
  error: string | null;
  successMessage: string | null;
}

/* One changed record as returned by PATCH api/ServiceRequest/visibility
 * (inside the standard { message, data } envelope). The server array is the
 * source of truth for what actually changed; the confirmation screen joins
 * these ids back to the in-memory staged SRs for display. Note the BE speaks
 * "external visibility", the inverse of our FE `isInternalOnly` model. */
export interface VisibilityUpdatedRecord {
  id: string;
  ticketNumber: string;
  subject: string;
  externalVisibilityOverride: boolean;
  isExternallyVisible: boolean;
}

export interface BulkUpdateVisibilityResult {
  ok: boolean;
  updatedCount: number;
  updatedRecords: VisibilityUpdatedRecord[];
}

export interface UseServiceRequestVisibilityApiReturn {
  isSubmitting: boolean;
  error: string | null;
  successMessage: string | null;
  bulkUpdateVisibility: (
    serviceRequestIds: string[],
    externalVisibilityOverride: boolean
  ) => Promise<BulkUpdateVisibilityResult>;
  clearError: () => void;
  clearSuccess: () => void;
}

const DEFAULT_VISIBILITY_ERROR_BY_STATUS: Record<number, string> = {
  400: 'No service requests were selected.',
  403: 'You do not have permission to change service request visibility.',
  404: 'None of the selected service requests could be found.',
  500: 'Something went wrong on our end. Please try again.',
};

const extractEnvelopeMessage = async (
  response: Response
): Promise<string | null> => {
  try {
    const body: any = await response.json();
    return body?.message ?? null;
  } catch {
    return null;
  }
};

const EMPTY_RESULT: BulkUpdateVisibilityResult = {
  ok: false,
  updatedCount: 0,
  updatedRecords: [],
};

export const useServiceRequestVisibilityApi = (): UseServiceRequestVisibilityApiReturn => {
  const [state, setState] = useState<UseServiceRequestVisibilityApiState>({
    isSubmitting: false,
    error: null,
    successMessage: null,
  });

  const bulkUpdateVisibility = async (
    serviceRequestIds: string[],
    externalVisibilityOverride: boolean
  ): Promise<BulkUpdateVisibilityResult> => {
    setState({ isSubmitting: true, error: null, successMessage: null });

    try {
      const response: any = await bulkUpdateServiceRequestVisibility(
        serviceRequestIds,
        externalVisibilityOverride
      );

      if (response.ok || response.status === 200) {
        const body: any = await response.json().catch(() => ({}));
        const data: any = body?.data ?? {};

        const updatedRecords: VisibilityUpdatedRecord[] = Array.isArray(
          data.serviceRequests
        )
          ? data.serviceRequests
          : [];

        const updatedCount: number =
          typeof data.updated === 'number'
            ? data.updated
            : updatedRecords.length;

        const successMessage: string = `Updated ${updatedCount} service request${
          updatedCount === 1 ? '' : 's'
        }.`;

        setState({ isSubmitting: false, error: null, successMessage });

        return { ok: true, updatedCount, updatedRecords };
      }

      const serverMessage: string | null =
        await extractEnvelopeMessage(response);

      const fallback: string =
        DEFAULT_VISIBILITY_ERROR_BY_STATUS[response.status] ??
        `Update failed. (Status ${response.status})`;

      setState({
        isSubmitting: false,
        error: serverMessage ?? fallback,
        successMessage: null,
      });

      return EMPTY_RESULT;
    } catch (err: any) {
      setState({
        isSubmitting: false,
        error:
          err?.message ??
          'Something went wrong while updating service request visibility.',
        successMessage: null,
      });

      return EMPTY_RESULT;
    }
  };

  const clearError = (): void => {
    setState((prev: UseServiceRequestVisibilityApiState) => ({
      ...prev,
      error: null,
    }));
  };

  const clearSuccess = (): void => {
    setState((prev: UseServiceRequestVisibilityApiState) => ({
      ...prev,
      successMessage: null,
    }));
  };

  return {
    isSubmitting: state.isSubmitting,
    error: state.error,
    successMessage: state.successMessage,
    bulkUpdateVisibility,
    clearError,
    clearSuccess,
  };
};